import {useCallback, useMemo, useState} from 'react'

// Molecules
import {PageHeader, SectionTitle} from '@/components/molecules'

// Organisms
import {CustomDataTable, PageContainer, UploadHistoryTableModal, UploadMediaModal} from '@/components/organisms'

// Helpers
import {getCellDataMappedCols} from '@/helpers/dataTableCellDataMapper'

// Hooks
import {useProductList} from '@/hooks/useProductList'

// Components
import {LinkText} from '@/components/atoms'

// Containers
import {ProductButtonContainer} from '@/containers/ProductButtonContainer'

// Constants
import {FONT_SIZE} from '@/utils/constants/globalStyle'

// Styles
import '@/assets/scss/custom/components/_productList.scss'
import {MAIN_COLUMNS} from '@/containers/Fulfillment/InventoryList/InventoryListConstants'
import {useProductButtonContainer} from '@/containers/ProductButtonContainer/ProductButtonContainer-hook'
import useUploadHistory from '@/hooks/useUploadHistory'
import {DEFAULT_TABLE_PAGE_SIZE} from '@/utils/constants/commonConstants'
import {PRODUCT_TYPE, UPLOAD_HISTORY_TYPE} from '@/utils/constants/globalConstants'
import {ROUTES} from '@/utils/constants/routesConstants'
import {useNavigate} from 'react-router-dom'

const cellDataMapperObj = navigate => ({
  productId: ({row: {original} = {}}) => {
    return (
      <div>
        <LinkText
          text={original?.productId}
          id={original?.productId + ' ' + original?.productId}
          handleClick={() => {
            navigate(`${ROUTES.PRODUCT_REVIEW.replace(':id', original?.productId)}`) // Replace :id with the actual productId
          }}
        />
      </div>
    )
  },

  productName: ({row: {original} = {}}) => {
    return (
      <div>
        <LinkText
          text={original?.productName}
          id={original?.productName + ' ' + original?.id}
          maxWidth="350px"
          showTooltip={true}
          handleClick={() => {
            navigate(`${ROUTES.PRODUCT_REVIEW.replace(':id', original?.productId)}`) // Replace :id with the actual productId
          }}
        />
      </div>
    )
  },
  displayName_en: ({row: {original} = {}}) => {
    return <div>{original?.displayName_en ?? '-'}</div>
  },
  subCategory_en: ({row: {original} = {}}) => {
    return <div>{original?.subCategory_en ?? '-'}</div>
  },
  showOnECommerce: ({row: {original} = {}}) => {
    return <div>{original?.showOnEcommerce ? 'Yes' : 'No'}</div>
  },
  suggestedProductSellingPrice: ({row: {original} = {}}) => {
    return <div>{original?.suggestedProductSellingPrice ?? '-'}</div>
  },
  multipleSellingUnits: ({row: {original} = {}}) => {
    return <div>{original?.multipleSellingUnits ?? '-'}</div>
  },
})

const subColumns = [
  {
    columnKey: 'productId',
    header: 'Product ID',
  },
  {
    columnKey: 'errorMsg',
    header: 'STATUS (REMARK)',
  },
]

export const ProductList = () => {
  const navigate = useNavigate()
  const [isSelectAll, setSelectAll] = useState(false)
  const [openUploadHistory, setOpenUploadHistory] = useState(false)
  const [productType, setProductType] = useState(PRODUCT_TYPE.BRAND)

  const [rowSelected, setRowSelected] = useState([])
  const {
    productListData,
    productListColConfigData,
    setSortingObj,
    setSearchArry,
    onDownloadBtnClick,
    onPageIndexChange,

    isLoading,
    createSearchArray,
    resetAllBtnClicked,
    fetchUploadErrors,
  } = useProductList()
  const {setFormData, formData, toggleMultipleImagesModal, isMultipleImagesModal} = useProductButtonContainer()
  const {onUploadHistoryPageIndexChange, uploadHistoryData, downloadUploadError} = useUploadHistory(
    UPLOAD_HISTORY_TYPE?.PRODUCT
  )

  const cellDataMappedCols = getCellDataMappedCols(cellDataMapperObj(navigate), productListColConfigData)
  const handleSorting = ({column, direction}) => {
    setSortingObj({column, direction})
  }

  // Handle row selection for CustomDataTable
  const handleRowSelect = selectedRows => {
    setRowSelected(selectedRows)
  }

  // Handle page index change for CustomDataTable
  const handlePageIndexChange = pageIndex => {
    onPageIndexChange(pageIndex)
  }
  const handleResetAll = () => {
    setSortingObj({})
    createSearchArray([])
    setSearchArry({})
    setRowSelected([])
  }
  const transformedColumns = useMemo(
    () =>
      cellDataMappedCols.map(col => ({
        name_of_db_column: col.name_of_db_column,
        label_of_column: col.label_of_column,
        filterVariant: col.filterVariant || 'text',
        // Add clickable configuration for productId
        ...(col.name_of_db_column === 'productId' && {
          isClickable: true,
          onCellClick: value => {
            if (value) {
              navigate(ROUTES.PRODUCT_REVIEW.replace(':id', value))
            }
          },
        }),
      })),
    [cellDataMappedCols, navigate]
  )
  const openProductType = useCallback(
    type => {
      setProductType(type)
      toggleMultipleImagesModal(true)
    },
    [toggleMultipleImagesModal, setProductType]
  )
  const tableActions = useMemo(
    () => [
      {
        id: 'brand',
        label: 'Upload Brand Images',
        onClick: () => openProductType(PRODUCT_TYPE.BRAND),
      },
      {
        id: 'subbrand',
        label: 'Upload Sub Brand Images',
        onClick: () => openProductType(PRODUCT_TYPE.SUBBRAND),
      },
      {
        id: 'displaybrand',
        label: 'Upload Display Brand Images',
        onClick: () => openProductType(PRODUCT_TYPE.DISPLAYBRAND),
      },
      {
        id: 'category',
        label: 'Upload Category Images',
        onClick: () => openProductType(PRODUCT_TYPE.CATEGORY),
      },
      {
        id: 'subcategory',
        label: 'Upload Sub Category Images',
        onClick: () => openProductType(PRODUCT_TYPE.SUBCATEGORY),
      },
      {
        id: 'displaycategory',
        label: 'Upload Display Category Images',
        onClick: () => openProductType(PRODUCT_TYPE.DISPLAYCATEGORY),
      },
      {
        id: 'view-upload-history',
        label: 'View Uploaded History',
        onClick: () => setOpenUploadHistory(true),
      },
      {
        id: 'download',
        label: 'Download',
        onClick: () => {
          // Use selectedRows from CustomDataTable if available, otherwise fall back to rowSelected
          const dataToDownload = isSelectAll ? [] : rowSelected
          const productIds = dataToDownload.map(product => product.productId)

          onDownloadBtnClick(productIds)
        },
        disabled: ({selectedRows}) => !selectedRows?.length, // Dynamic disable based on selection
      },
    ],
    [openProductType, isSelectAll, rowSelected, onDownloadBtnClick]
  )

  const columns = MAIN_COLUMNS(downloadUploadError)

  return (
    <div className="ProductListContainer">
      <PageHeader title="Products" fontSize={FONT_SIZE.XXXL} />
      <ProductButtonContainer />
      <PageContainer data-testid="create-bulk-point-modifiers-content">
        <div>
          <SectionTitle fontSize={FONT_SIZE.XXL} label={'Products list'} />
        </div>

        <div className="ProductListDataTable">
          {/* {productListColConfigData?.length > 0 && productListData?.data && ( */}
          <CustomDataTable
            columns={transformedColumns}
            records={productListData?.data || []}
            setSearchArry={setSearchArry}
            handleSorting={handleSorting}
            setPageIndex={handlePageIndexChange}
            totalRecords={productListData?.totalRecords || 0}
            pageSize={DEFAULT_TABLE_PAGE_SIZE}
            onResetAll={() => {
              handleResetAll()
              resetAllBtnClicked()
            }}
            onRowSelect={handleRowSelect}
            isLoading={isLoading}
            showActions={true}
            id="productId"
            actions={tableActions}
            isSelectAll={isSelectAll}
            setSelectAll={setSelectAll}
            defaultSortConfig={{key: 'productId', direction: 'desc'}}
          />
        </div>
      </PageContainer>
      <UploadHistoryTableModal
        openUploadHistory={openUploadHistory}
        setOpenUploadHistory={setOpenUploadHistory}
        mainData={uploadHistoryData.data}
        totalRecords={uploadHistoryData.totalRecords}
        mainColumns={columns}
        subColumns={subColumns}
        fetchSubRows={fetchUploadErrors}
        isDark={true}
        setPageIndex={onUploadHistoryPageIndexChange}
        data-testid="upload-history-modal"
      />
      <UploadMediaModal
        openUpload={isMultipleImagesModal}
        setOpenUpload={toggleMultipleImagesModal}
        title="Upload Media"
        showTertiaryButton={true}
        formData={formData}
        setFormData={setFormData}
        entityType={productType}
      />
    </div>
  )
}
